import { Injectable, WritableSignal, inject, signal } from "@angular/core";
import { ExamComponentForPlanCourse } from "@app/core/domain/exam-component";
import { PlanHelper } from "@app/core/domain/helpers/plan-helper";
import { EducationService } from "@app/features/education/services/education.service";
import { Constants } from "@core/constants";
import { Course } from "@core/domain/course";
import { CourseDetail } from "@core/domain/course-detail";
import { CourseHelper } from "@core/domain/helpers/course-helper";
import { CourseService } from "@core/services/course.service";
import { OverlayEventDetail } from "@ionic/core";
import { TranslateService } from "@ngx-translate/core";
import { FlexiblePlanElementModalComponent } from "@shared/modals/flexible-plan-element-modal/flexible-plan-element-modal.component";
import { ScheduleCourseModalComponent } from "@shared/modals/schedule-course-modal/schedule-course-modal.component";
import { LoadingService } from "@shared/services/loading-service";
import { ModalInput, ModalService } from "@shared/services/modal.service";
import { PlanLaterStateService } from "@shared/services/plan-later-state.service";
import { PlanStateService } from "@shared/services/plan-state.service";
import { ToastService } from "@shared/services/toast-service";
import { CourseData, SelectedPeriod } from "@shared/types/course-data";
import { CourseUpdateAction } from "@shared/types/plan-actions";
import { Subject, switchMap, tap } from "rxjs";
import { ChangeExamComponentModalComponent } from "../modals/change-exam-component-modal/change-exam-component-modal.component";
import { PlanLaterType } from "../types/plan-later-types";

@Injectable({
  providedIn: "root",
})
export class PlanActionService {
  private readonly planLaterStateService = inject(PlanLaterStateService);
  private readonly planStateService = inject(PlanStateService);
  private readonly planHelper = inject(PlanHelper);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly courseHelper = inject(CourseHelper);
  private readonly modalService = inject(ModalService);

  private readonly courseService = inject(CourseService);
  private readonly loadingService = inject(LoadingService);
  private readonly educationService = inject(EducationService);

  // Signal that can be used to update the course data after a plan action (planNow/planLater/remove)
  courseUpdated: WritableSignal<CourseUpdateAction | undefined> = signal(undefined);

  signalCourseUpdated(course: Course, selectedPeriod?: SelectedPeriod): void {
    this.courseUpdated.set({
      course: course,
      selectedPeriod,
    });
  }

  // Event that can be used to update the user interface
  // when the plan is replaced, and the courses in the grid remained the same,
  // but the plan and the courses might have other statuses now
  // (mostly related to submitting proposals)
  // ATTENTION: don't forget to unsubscribe in the ngOnDestroy.
  // It would maybe be preferable to use a signal instead of a Subject, but then
  // we would have to mimic that each time a value is set, it is seen as a new value,
  // using the technique of https://stackoverflow.com/questions/78830698/send-a-signal-with-the-same-value
  newPlanWithUpdatedStatusesEvent = new Subject<void>();
  signalNewPlanWithUpdatedStatuses(): void {
    this.newPlanWithUpdatedStatusesEvent.next();
  }

  /**
   * Move a course that is already in the plan grid to a different period.
   */
  private moveCourse(courseData: CourseData, selectedPeriod: SelectedPeriod): void {
    this.planStateService.removeCourseFromPlan(courseData, false);
    this.planCourse(courseData, selectedPeriod);
  }

  /**
   * Add a course to the plan grid, and if needed remove it from Plan Later.
   */
  private planCourse(courseData: CourseData, selectedPeriod: SelectedPeriod): void {
    if (this.planLaterStateService.isCourseInPlanLaterOtherCourses(courseData.code)) {
      // Remove from Plan Later if it was there in "Other courses".
      // Modify the course data because if the planning was done from outside the plan page,
      // the plan later type is not set
      courseData.planLaterType = PlanLaterType.OTHER_COURSES;
      this.planLaterStateService.removeCourse(courseData);
    }
    this.planStateService.addCourseToPeriod(courseData, selectedPeriod);
    courseData.isPlannable = false;
    courseData.plannedText = this.courseHelper.getPlannedText(courseData);

    this.signalCourseUpdated(courseData, selectedPeriod);

    this.toastService
      .presentToast(
        this.translate.instant("courseSchedule.scheduled", {
          course: `${courseData.code} ${this.courseHelper.getCourseName(courseData)}`,
          year: selectedPeriod.year.yearNr,
          period_oms: selectedPeriod.period.periodDescription,
        }),
      )
      .catch((error) => {
        console.error("Error presenting toast for planCourse:", error);
      });
  }

  planNow(courseData: CourseData): void {
    this.loadingService
      .present()
      .pipe(
        // custom courses never have a Plan Now option, so the course code is always defined
        switchMap(() => this.courseService.fetchCourse(courseData.code!, courseData.planId)),
        tap(() => this.loadingService.dismiss()),
      )
      .subscribe((courseDetail) => {
        if (courseDetail) {
          this.selectPeriodAndPlan(courseData, courseDetail).catch((error) => {
            console.error("Error in selectPeriodAndPlan while executing planNow:", error);
          });
        }
      });
  }

  async selectPeriodAndPlan(
    courseData: CourseData,
    courseDetail: CourseDetail,
  ): Promise<CourseUpdateAction | undefined> {
    const modal = await this.getScheduleCourseModal(courseData, courseDetail);
    await modal.present();
    const result = await modal.onWillDismiss();

    if (result.role !== Constants.BUTTON_ACTION_OK) {
      return undefined;
    }

    const selectedPeriod = result.data as SelectedPeriod;
    this.updateCourseDataWithSelectedPeriod(courseData, selectedPeriod);
    const updatedCourseData = this.doPlanNow(courseData, selectedPeriod);

    return {
      course: updatedCourseData,
      selectedPeriod: selectedPeriod,
    } as CourseUpdateAction;
  }

  /**
   * Put a course in Plan Later.
   * If the course is already in one of the special sections of plan later, we don't need to do anything.
   * Otherwise, we add it to the "Other courses" section of plan later.
   */
  planLater(courseData: CourseData): CourseData {
    if (!this.planLaterStateService.findCourseInPlanLater(courseData.code)) {
      courseData.isAddedToPlanLater = true;
      courseData.isPartOfPlanLater = true;
      courseData.academicYear = 0;
      courseData.periodNr = 0;
      courseData.periodDescription = "";
      courseData.isInPlanLater = true;
      courseData.id = undefined;

      this.planLaterStateService.addOtherCourse(courseData);
      this.planStateService.markPlanAsModified(true);
    }

    this.toastService
      .presentToast(
        this.translate.instant("courseMove.movedToPlanLater", {
          course: `${courseData.code} ${this.courseHelper.getCourseName(courseData)}`,
        }),
      )
      .catch((error) => {
        console.error("Error presenting toast for moved to plan later:", error);
      });

    return courseData;
  }

  private doPlanNow(courseData: CourseData, selectedPeriod: SelectedPeriod): CourseData {
    courseData.isInPlanLater = false;

    if (courseData.isCustomCourse) {
      courseData.academicYear = 0;
    }
    if (
      courseData.academicYear !== selectedPeriod.year.academicYear ||
      courseData.periodNr !== selectedPeriod.period.periodNr ||
      courseData.isFlexCourse
    ) {
      if (courseData.isPlannedCourse) {
        // it was already in the plan grid, but we are moving it to a different period
        this.moveCourse(courseData, selectedPeriod);
      } else {
        // it was not in the plan grid, so we are adding it
        const plan = this.planStateService.currentSelectedPlan()!;
        if (plan.allowsChangeExamComponent) {
          // if placing in exam components is via the PlanApp, and the course exists in Plan Later,
          // and the course is in a required exam component (not an OR-branch),
          // take the exam component of Plan Later (so that required courses remain in their required exam component),
          // otherwise put it in the "Other" exam component
          // (so that the course is not automatically placed in the curriculum exam component if planned from there)
          const courseInPlanLater = this.planLaterStateService.findCourseInPlanLater(courseData.code);
          if (courseInPlanLater?.isInRequiredExamComponent) {
            courseData.examComponentCode = courseInPlanLater.examComponentCode;
            courseData.examComponentName = courseInPlanLater.examComponentName;
            courseData.specialisationProgram = courseInPlanLater.specialisationProgram;
            courseData.specialisationName = courseInPlanLater.specialisationName;
            courseData.minorProgram = courseInPlanLater.minorProgram;
            courseData.minorName = courseInPlanLater.minorName;
            courseData.labelColor = courseInPlanLater.labelColor; // the color for this exam component
          } else {
            courseData.examComponentCode = Constants.EXAM_COMPONENT_OTHER;
            courseData.examComponentName = "";
            courseData.specialisationProgram = "";
            courseData.specialisationName = "";
            courseData.minorProgram = "";
            courseData.minorName = "";
            courseData.labelColor = "";
          }
        }
        this.planCourse(courseData, selectedPeriod);
      }
    }

    courseData.isRibbonCourse = this.courseHelper.isRibbonCourse(courseData);

    return courseData;
  }

  removeCourseFromPlan(course: Course): void {
    this.planStateService.removeCourseFromPlan(course);
    this.signalCourseUpdated(course);
  }

  removeCourseFromPlanLater(courseData: CourseData): void {
    this.planLaterStateService.removeCourse(courseData);
  }

  moveCourseFromCourseInfo(courseData: CourseData, selectedPeriod: SelectedPeriod): void {
    this.doPlanNow(courseData, selectedPeriod);
  }

  async moveCourseFromPlanGrid(courseData: CourseData, courseDetail: CourseDetail): Promise<void> {
    const modal = await this.getScheduleCourseModal(courseData, courseDetail, {
      // only when called from "Move" menu item of a course already on the plan grid,
      // the "plan now" modal should show the "move to plan later" button
      showMoveToPlanLaterButton: true,
    });
    await modal.present();
    const result = await modal.onWillDismiss();

    const selectedPeriod = result.data as SelectedPeriod;

    switch (result.role) {
      case Constants.BUTTON_ACTION_PLANLATER:
        this.removeCourseFromPlan(courseData);
        this.planLater(courseData);
        courseData.plannedText = this.courseHelper.getPlannedText(courseData);
        break;
      case Constants.BUTTON_ACTION_OK:
        this.updateCourseDataWithSelectedPeriod(courseData, selectedPeriod);
        this.doPlanNow(courseData, selectedPeriod);
        break;
    }
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async openChangeExamComponentModal(course: Course): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.educationService.getExamComponentsForPlanCourse(course.id!).subscribe(async (examComponents) => {
      // add OVERIG (Other, meaning not in any study program) as an option to choose from
      examComponents.push({
        examComponentCode: Constants.EXAM_COMPONENT_OTHER,
        examComponentName: this.translate.instant("changeExamComponent.notInStudyProgram"),
        studyProgram: "",
        specialisationProgram: "",
        specialisationName: "",
        minorProgram: "",
        minorName: "",
      });
      this.planHelper.addUniqueIdsToExamComponents(examComponents);
      const modal = await this.getChangeExamComponentModal(course, examComponents);
      await modal.present();
      // process the modal result
      const dismissResult: OverlayEventDetail = await modal.onWillDismiss();
      const newExamComponentForPlanCourse = dismissResult.data as ExamComponentForPlanCourse | null;
      this.processNewExamComponentForCourse(newExamComponentForPlanCourse, course);
    });
  }

  // public for unit tests
  processNewExamComponentForCourse(
    newExamComponentForPlanCourse: ExamComponentForPlanCourse | null,
    course: Course,
  ): void {
    if (
      newExamComponentForPlanCourse &&
      (course.examComponentCode !== newExamComponentForPlanCourse.examComponentCode ||
        course.studyProgram !== newExamComponentForPlanCourse.studyProgram ||
        course.specialisationProgram !== newExamComponentForPlanCourse.specialisationProgram ||
        course.minorProgram !== newExamComponentForPlanCourse.minorProgram)
    ) {
      this.updateCourseWithNewExamComponent(course, newExamComponentForPlanCourse);
      this.changeExamComponentOfRibbonRepeats(course, newExamComponentForPlanCourse);
      this.planStateService.markPlanAsModified(true);
    }
  }

  private updateCourseWithNewExamComponent(
    course: Course,
    newExamComponentForPlanCourse: ExamComponentForPlanCourse,
  ): void {
    course.examComponentCode = newExamComponentForPlanCourse.examComponentCode;
    course.studyProgram = newExamComponentForPlanCourse.studyProgram;
    course.specialisationProgram = newExamComponentForPlanCourse.specialisationProgram;
    course.minorProgram = newExamComponentForPlanCourse.minorProgram;
    // ensure that the new exam component description is immediately visible
    if (course.examComponentCode === Constants.EXAM_COMPONENT_OTHER) {
      course.examComponentName = "";
      course.specialisationName = "";
      course.minorName = "";
    } else {
      course.examComponentName = newExamComponentForPlanCourse.examComponentName;
      course.specialisationName = newExamComponentForPlanCourse.specialisationName;
      course.minorName = newExamComponentForPlanCourse.minorName;
    }
    this.signalCourseUpdated(course);
  }

  private changeExamComponentOfRibbonRepeats(
    course: Course,
    newExamComponentForPlanCourse: ExamComponentForPlanCourse,
  ): void {
    if (this.courseHelper.isRibbonCourse(course)) {
      const plan = this.planStateService.currentSelectedPlan()!;
      // find the year of the plan in which the ribbon course occurs
      const year = plan.studyYears.find((thisYear) => thisYear.yearNr === course.yearNr)!;
      // find the periods in the year in which the ribbon course occurs, exclude the first
      const ribbonRepeatPeriods = year.periods.filter(
        (period) => course.ribbonPeriods.includes(period.periodNr) && course.periodNr !== period.periodNr,
      );

      ribbonRepeatPeriods.forEach((ribbonRepeatPeriod) => {
        // find the ribbon repeat course in the period
        const ribbonRepeatCourse = ribbonRepeatPeriod.courses.find(
          (thisCourse) => thisCourse.code === course.code && thisCourse.isRibbonRepeat,
        )!;
        this.updateCourseWithNewExamComponent(ribbonRepeatCourse, newExamComponentForPlanCourse);
      });
    }
  }

  private async getChangeExamComponentModal(
    course: Course,
    examComponentsToChooseFrom: ExamComponentForPlanCourse[],
  ): Promise<HTMLIonModalElement> {
    const modalInput: ModalInput = {
      component: ChangeExamComponentModalComponent,
      componentProps: {
        course: course,
        examComponentsToChooseFrom: examComponentsToChooseFrom,
      },
      cssClass: "change-exam-component-modal",
    };

    return this.modalService.createModal(modalInput);
  }

  private async getScheduleCourseModal(
    courseData: CourseData,
    courseDetail: CourseDetail,
    options: { showMoveToPlanLaterButton?: boolean } = {},
  ): Promise<HTMLIonModalElement> {
    const modalInput: ModalInput = {
      component: courseDetail.isFlexCourse ? FlexiblePlanElementModalComponent : ScheduleCourseModalComponent,
      componentProps: {
        courseData: courseData,
        courseDetail: courseDetail,
        showMoveToPlanLaterButton: options.showMoveToPlanLaterButton,
      },
      cssClass: courseDetail.isFlexCourse ? "flexible-plan-modal" : "schedule-course-modal",
    };

    return this.modalService.createModal(modalInput);
  }

  private updateCourseDataWithSelectedPeriod(courseData: CourseData, selectedPeriod: SelectedPeriod) {
    const hasRibbonPeriods = selectedPeriod.period.ribbonPeriods?.length > 1;
    courseData.ribbonPeriods = hasRibbonPeriods ? selectedPeriod.period.ribbonPeriods : [];

    courseData.timeslots = selectedPeriod.period.timeslots?.length ? selectedPeriod.period.timeslots : [];
  }
}
